import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  Stack,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EventIcon from '@mui/icons-material/Event';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import CalculateIcon from '@mui/icons-material/Calculate';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAuth } from '../../../contexts/AuthContext';
import { getSurvey, transitionSurveyStatus, updateSurvey } from '../../../services/surveyService';
import CreateSurveyDialog from '../components/CreateSurveyDialog';
import { useUserOrganizations } from '../hooks/useOrganization';
import EditIcon from '@mui/icons-material/Edit';
import { getResponsesForSurvey, getDraftResponse } from '../../../services/responseService';
import { createOESnapshot } from '../../../services/oeSnapshotService';
import { exportSurveyResponsesCSV } from '../utils/csvExport';
import { STANDARD_QUESTION_COUNT } from '../utils/constants';

const STATUS_COLORS = {
  draft: 'default',
  active: 'success',
  closed: 'error',
};

function formatDate(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function SurveyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const organization = organizations[0] || null;
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exporting, setExporting] = useState(false);
  const [responses, setResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null });
  const [hasDraft, setHasDraft] = useState(false);
  const [calculatingOE, setCalculatingOE] = useState(false);

  // Check for incomplete/draft response for current user
  useEffect(() => {
    if (!id || !user?.uid) return;
    getDraftResponse(id, user.uid)
      .then((draft) => {
        setHasDraft(!!draft);
      })
      .catch(() => {
        setHasDraft(false);
      });
  }, [id, user?.uid]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getSurvey(id)
      .then((s) => {
        setSurvey(s);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  // Fetch responses
  useEffect(() => {
    if (!id) return;
    setResponsesLoading(true);
    getResponsesForSurvey(id)
      .then((r) => {
        setResponses(r);
        setResponsesLoading(false);
      })
      .catch(() => {
        setResponsesLoading(false);
      });
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    setConfirmDialog({ open: false, action: null });
    setStatusChanging(true);
    try {
      await transitionSurveyStatus(id, newStatus);
      setSurvey((prev) => ({ ...prev, status: newStatus }));
      const actionLabel = newStatus === 'closed' ? 'closed' : newStatus === 'active' && survey.status === 'draft' ? 'published' : 'reopened';

      // When closing a survey, generate an OE snapshot from its responses
      if (newStatus === 'closed') {
        try {
          const freshResponses = await getResponsesForSurvey(id);
          if (freshResponses.length > 0) {
            await createOESnapshot({
              organizationId: survey.organizationId,
              scope: { type: 'organization', id: survey.organizationId },
              responses: freshResponses,
            });
          }
        } catch (snapshotErr) {
          console.error('Failed to create OE snapshot:', snapshotErr);
          // Non-blocking — survey was still closed successfully
        }
      }

      setSnackbar({
        open: true,
        message: `Survey ${actionLabel} successfully.`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setStatusChanging(false);
    }
  };

  const shareLink = `${window.location.origin}/drew/surveys/${id}/take`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setSnackbar({ open: true, message: 'Survey link copied to clipboard!', severity: 'success' });
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setSnackbar({ open: true, message: 'Survey link copied to clipboard!', severity: 'success' });
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const responses = await getResponsesForSurvey(id);
      if (!responses || responses.length === 0) {
        setSnackbar({ open: true, message: 'No responses to export.', severity: 'warning' });
        return;
      }
      exportSurveyResponsesCSV(responses, survey);
      setSnackbar({ open: true, message: `Exported ${responses.length} response(s) to CSV.`, severity: 'success' });
    } catch (err) {
      console.error('CSV export error:', err);
      setSnackbar({ open: true, message: 'Failed to export responses: ' + err.message, severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleCalculateOE = async () => {
    setCalculatingOE(true);
    try {
      const freshResponses = await getResponsesForSurvey(id);
      if (!freshResponses || freshResponses.length === 0) {
        setSnackbar({ open: true, message: 'No responses to calculate OE scores from.', severity: 'warning' });
        return;
      }
      await createOESnapshot({
        organizationId: survey.organizationId,
        scope: { type: 'organization', id: survey.organizationId },
        responses: freshResponses,
      });
      setSnackbar({ open: true, message: `OE scores calculated from ${freshResponses.length} response(s). Check the dashboard!`, severity: 'success' });
    } catch (err) {
      console.error('OE calculation error:', err);
      setSnackbar({ open: true, message: 'Failed to calculate OE scores: ' + err.message, severity: 'error' });
    } finally {
      setCalculatingOE(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">Failed to load survey: {error}</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/drew/surveys')}
        >
          Back to Surveys
        </Button>
      </Box>
    );
  }

  if (!survey) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="warning">Survey not found.</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/drew/surveys')}
        >
          Back to Surveys
        </Button>
      </Box>
    );
  }

  const isActive = survey.status === 'active';

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/drew/surveys')}
        sx={{ mb: 2 }}
      >
        Back to Surveys
      </Button>

      {/* Survey header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Typography variant="h4" component="h1">
              {survey.title}
            </Typography>
            <Chip
              label={survey.status}
              color={STATUS_COLORS[survey.status] || 'default'}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
            {survey.anonymous && (
              <Chip
                icon={<VisibilityOffIcon />}
                label="Anonymous"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          {survey.description && (
            <Typography variant="body1" color="text.secondary">
              {survey.description}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Survey metadata */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong> {formatDate(survey.createdAt)}
              </Typography>
              {survey.dueDate && (
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: (() => {
                      const due = new Date(survey.dueDate);
                      due.setHours(23, 59, 59, 999);
                      return due < new Date() ? 'warning.main' : 'text.secondary';
                    })(),
                  }}
                >
                  <EventIcon sx={{ fontSize: 16 }} />
                  <strong>Due:</strong> {new Date(survey.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                <strong>Question Set:</strong> {survey.questionSet === 'standard' ? `Standard (${STANDARD_QUESTION_COUNT} questions)` : 'Custom'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Responses:</strong> {Math.max(responses.length, survey.responseCount || 0)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Action buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {isActive && (
          <Button
            variant="contained"
            color={hasDraft ? 'info' : 'primary'}
            startIcon={hasDraft ? <ReplayIcon /> : <PlayArrowIcon />}
            onClick={() => navigate(`/drew/surveys/${id}/take`)}
          >
            {hasDraft ? 'Resume Survey' : 'Take Survey'}
          </Button>
        )}

        {/* Share button - only for active surveys */}
        {isActive && (
          <Tooltip title={copied ? 'Copied!' : 'Copy shareable link to clipboard'}>
            <Button
              variant="outlined"
              startIcon={copied ? <CheckIcon /> : <ShareIcon />}
              onClick={handleCopyLink}
              color={copied ? 'success' : 'primary'}
            >
              {copied ? 'Link Copied!' : 'Share Survey'}
            </Button>
          </Tooltip>
        )}

        {/* Export CSV button - available for any survey with responses */}
        <Button
          variant="outlined"
          startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
          onClick={handleExportCSV}
          disabled={exporting}
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>

        {/* Calculate OE Scores button - for closed surveys with responses */}
        {survey.status === 'closed' && responses.length > 0 && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={calculatingOE ? <CircularProgress size={18} /> : <CalculateIcon />}
            onClick={handleCalculateOE}
            disabled={calculatingOE}
          >
            {calculatingOE ? 'Calculating...' : 'Calculate OE Scores'}
          </Button>
        )}

        {/* Tim 26 Oct (D05 r1): a draft's details are editable until it is published */}
        {survey.status === 'draft' && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
            Edit Draft
          </Button>
        )}
        {/* Status transition actions */}
        {survey.status === 'draft' && (
          <Button
            variant="contained"
            color="success"
            startIcon={statusChanging ? <CircularProgress size={18} /> : <PlayArrowIcon />}
            onClick={() => setConfirmDialog({ open: true, action: 'active' })}
            disabled={statusChanging}
          >
            Publish Survey
          </Button>
        )}
        {survey.status === 'active' && (
          <Button
            variant="outlined"
            color="error"
            startIcon={statusChanging ? <CircularProgress size={18} /> : <LockIcon />}
            onClick={() => setConfirmDialog({ open: true, action: 'closed' })}
            disabled={statusChanging}
          >
            Close Survey
          </Button>
        )}
        {survey.status === 'closed' && (
          <Button
            variant="outlined"
            color="success"
            startIcon={statusChanging ? <CircularProgress size={18} /> : <LockOpenIcon />}
            onClick={() => setConfirmDialog({ open: true, action: 'active' })}
            disabled={statusChanging}
          >
            Reopen Survey
          </Button>
        )}
      </Stack>

      {/* Share link display for active surveys */}
      {isActive && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Shareable Link
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Share this link with respondents. They must be authenticated to take the survey.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  fontSize: '0.8rem',
                }}
              >
                {shareLink}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={handleCopyLink}
                  color={copied ? 'success' : 'primary'}
                  sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Status message for non-active surveys */}
      {!isActive && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {survey.status === 'draft'
            ? 'This survey is in draft mode. Publish it to generate a shareable link and allow respondents to take it.'
            : 'This survey is closed. No new responses can be submitted.'}
        </Alert>
      )}

      {/* Individual Responses List */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Responses ({responses.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {responsesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : responses.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No responses yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Respondent</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>R</TableCell>
                    <TableCell>E</TableCell>
                    <TableCell>P</TableCell>
                    <TableCell>S</TableCell>
                    <TableCell>Completed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {responses.map((r, idx) => {
                    const scope = r.respondentScope || {};
                    const reps = r.repsScores || {};
                    return (
                      <TableRow key={r.id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{r.respondentUid || 'Anonymous'}</TableCell>
                        <TableCell>{scope.siteName || scope.siteId || '—'}</TableCell>
                        <TableCell>{scope.departmentName || scope.departmentId || '—'}</TableCell>
                        <TableCell>{scope.teamName || scope.teamId || '—'}</TableCell>
                        <TableCell>{reps.reliability !== undefined ? reps.reliability.toFixed(1) : '—'}</TableCell>
                        <TableCell>{reps.efficiency !== undefined ? reps.efficiency.toFixed(1) : '—'}</TableCell>
                        <TableCell>{reps.productivity !== undefined ? reps.productivity.toFixed(1) : '—'}</TableCell>
                        <TableCell>{reps.safety !== undefined ? reps.safety.toFixed(1) : '—'}</TableCell>
                        <TableCell>{formatDate(r.completedAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog for status changes */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null })}
      >
        <DialogTitle>
          {confirmDialog.action === 'closed' ? 'Close Survey' : survey?.status === 'draft' ? 'Publish Survey' : 'Reopen Survey'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'closed'
              ? 'Are you sure you want to close this survey? No new responses will be accepted.'
              : survey?.status === 'draft'
                ? 'Are you sure you want to publish this survey? It will become active and respondents will be able to submit responses.'
                : 'Are you sure you want to reopen this survey? Respondents will be able to submit new responses.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null })}>Cancel</Button>
          <Button
            onClick={() => handleStatusChange(confirmDialog.action)}
            color={confirmDialog.action === 'closed' ? 'error' : 'success'}
            variant="contained"
          >
            {confirmDialog.action === 'closed' ? 'Close Survey' : survey?.status === 'draft' ? 'Publish Survey' : 'Reopen Survey'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy feedback */}
      <CreateSurveyDialog
        open={editOpen}
        initial={survey}
        organization={organization}
        onClose={() => setEditOpen(false)}
        onSave={async (data) => {
          const { status, ...fields } = data;
          await updateSurvey(survey.id, fields);
          if (status === 'active') await transitionSurveyStatus(survey.id, 'active');
          setSurvey((prev) => ({ ...prev, ...fields, ...(status === 'active' ? { status: 'active' } : {}) }));
        }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SurveyDetail;
